import React, { useState } from "react";
import { Button, Icon } from "aible";
import { humanizeError } from "types/expression";
import * as RelationUtils from "utils/relation";
import * as ExpressionUtils from "utils/expression";
import { getDefaultSections, SECTION_KEYS } from "containers/Relation/reducer";
import RelationInput from "./RelationInput";
import ReplayInput from "./ReplayInput";
import JoinIcon from "images/Sense-Icons/JoinIcon.svg";
import AppendIcon from "images/Sense-Icons/AppendIcon.svg";
import DerivedIcon from "images/Sense-Icons/DerivedIcon.svg";
import FilterIcon from "images/Sense-Icons/FilterIcon.svg";
import IfElseIcon from "images/Sense-Icons/IfElse.svg";
import Code from "images/Sense-Icons/Code.svg";
import Sliders from "images/Sense-Icons/Sliders.svg";
import Sparkles from "images/Sense-Icons/Sparkles.svg";
import BanIcon from "images/Sense-Icons/ban-light.svg";
import PivotIcon from "images/Sense-Icons/angle-90-light.svg";
import DateIcon from "images/EDG/DateIcon.svg";
import styles from "aible/SenseAccordion/style.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo } from "@fortawesome/free-solid-svg-icons";
import QueryPreview from "./QueryPreview";
import AggregateRelationInput from "./AggregateRelationInput";
import MutateRelationInput from "./MutateRelationInput";
import { isEnabled } from "@/utils/featureFlags";
const AccordionItem = ({ children, isOpen, setIsOpen, headerLabel, headerImageSrc, isOverflowVisible, isOverflowHidden, isNoMaxHeight, isCenter, errors, showErrors, onUndoChanges, canUndoChanges, onSaveChanges }) => {
    return (React.createElement("div", { className: styles.accordionWrapper },
        React.createElement("div", { className: styles.accordionHeader, onClick: () => setIsOpen(!isOpen), style: isOpen ? { borderBottom: "1px solid transparent" } : {} },
            React.createElement("div", { style: { display: "flex", alignItems: "center", flexGrow: 1 } },
                React.createElement("div", { className: styles.aIcon },
                    React.createElement("img", { src: headerImageSrc })),
                React.createElement("div", { style: { display: "flex", flexGrow: 1, justifyContent: "space-between" } },
                    React.createElement("div", null, headerLabel),
                    React.createElement("div", { style: { display: "flex", alignItems: "center" } },
                        canUndoChanges && onSaveChanges !== undefined && (React.createElement(Button, { style: { padding: "4px", marginRight: "5px", fontSize: "12px", lineHeight: "12px" }, onClick: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onSaveChanges();
                            }, title: errors.length > 0 ? errors.map((e) => humanizeError(e)).join(", ") : undefined, disabled: errors.length > 0 }, "Move to Pending")),
                        canUndoChanges && onUndoChanges !== undefined && (React.createElement(Button, { style: { padding: "4px", marginRight: "5px", fontSize: "12px", lineHeight: "12px" }, onClick: (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onUndoChanges();
                            }, disabled: canUndoChanges !== true },
                            React.createElement(FontAwesomeIcon, { icon: faUndo })))))),
            React.createElement("div", { className: styles.accordionIndicator },
                React.createElement(Icon, { type: isOpen ? "chevronUp" : "chevronDown" }))),
        React.createElement("div", { className: styles.accordionBody +
                (isOpen ? "" : " " + styles.hide) +
                " " +
                (isOverflowVisible ? styles.oVisible : "") +
                " " +
                (isOverflowHidden ? styles.oHidden : "") +
                " " +
                (isCenter ? styles.oCenter : "") +
                " " +
                (isNoMaxHeight ? styles.noMaxHeight : ""), style: isOpen
                ? {
                    borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                    paddingBottom: "10px",
                    paddingLeft: "2px",
                    fontSize: "12px"
                }
                : { borderBottom: "1px solid transparent", fontSize: "12px" } },
            isOpen &&
                showErrors &&
                errors.length > 0 &&
                errors.map((error, i) => React.createElement("div", { key: i }, humanizeError(error))),
            isOpen && children)));
};
const SECTION_LABELS = {
    apply: "Apply From Another Dataset",
    project: "Update Columns",
    unary: "Modify Column Values",
    date: "Extract Date/Time Values",
    join: "Join",
    distinct: "Deduplicate Rows",
    filter: "Filter In Rows",
    union: "Append",
    binary: "Create Derived Fields",
    case: "Conditional Derived Fields (If-Then-Else)",
    pivot: "Reshape the Data (Pivot / Unpivot)",
    aggregate: "Aggregate Data",
    sql: "Custom SQL"
};
const SECTION_IMAGES = {
    apply: Sparkles,
    project: Sliders,
    unary: DerivedIcon,
    date: DateIcon,
    join: JoinIcon,
    distinct: BanIcon,
    filter: FilterIcon,
    union: AppendIcon,
    binary: DerivedIcon,
    case: IfElseIcon,
    pivot: PivotIcon,
    aggregate: PivotIcon,
    sql: Code
};
const PROJECTION_FACTORY = ExpressionUtils.defaultProjectionFactory();
const SECTION_EXPR_PROVIDERS = {
    apply: undefined,
    project: undefined,
    unary: () => PROJECTION_FACTORY.unaryFunction(),
    date: () => PROJECTION_FACTORY.datePart(),
    join: undefined,
    distinct: undefined,
    filter: undefined,
    union: undefined,
    binary: () => PROJECTION_FACTORY.binaryOperation(),
    case: () => PROJECTION_FACTORY.ifThen(),
    pivot: undefined,
    aggregate: () => PROJECTION_FACTORY.aggregateMeasure(),
    sql: undefined
};
const SectionDraftInput = ({ sectionKey, relation, onChange, exprProvider, isDisabled }) => {
    if (sectionKey == "apply") {
        return React.createElement(ReplayInput, { relation: relation, onChange: onChange, isDisabled: isDisabled });
    }
    else if (relation.type == "MUTATE") {
        return (React.createElement(MutateRelationInput, { relation: relation, onChange: onChange, exprProvider: exprProvider, isDisabled: isDisabled }));
    }
    else if (relation.type == "AGGREGATE") {
        return (React.createElement(AggregateRelationInput, { relation: relation, onChange: onChange, exprProvider: () => PROJECTION_FACTORY.aggregateMeasure(), isDisabled: isDisabled }));
    }
    return React.createElement(RelationInput, { relation: relation, onChange: onChange, isDisabled: isDisabled });
};
const RelationAccordion = ({ datasetId, draft, updateSectionDraft, rebaseDraftOnSection }) => {
    const [openItems, updateOpenItems] = useState({});
    const [resetCounters, updateResetCounters] = useState({});
    const defaultState = getDefaultSections(draft.base);
    const baseList = RelationUtils.toList(draft.base);
    const setItemOpen = (key, x) => {
        updateOpenItems({ ...openItems, [key]: x });
    };
    return (React.createElement("div", null,
        SECTION_KEYS.filter((key) => {
            return key != "sql" || isEnabled("sql-transforms");
        }).map((key) => (React.createElement(AccordionItem, { key: `${key}_${baseList.length}_${resetCounters[key]}`, isOpen: openItems[key], setIsOpen: (x) => setItemOpen(key, x), headerImageSrc: SECTION_IMAGES[key], headerLabel: SECTION_LABELS[key], errors: RelationUtils.shallowValidate(draft.sections[key]), onSaveChanges: () => rebaseDraftOnSection(key), canUndoChanges: !RelationUtils.isEqual(draft.sections[key], defaultState[key]), onUndoChanges: () => {
                updateSectionDraft(key, defaultState[key]);
                updateResetCounters({
                    ...resetCounters,
                    [key]: (resetCounters[key] ?? 0) + 1
                });
            }, isOverflowVisible: ["union", "filter", "distinct", "pivot", "apply", "sql"].indexOf(key) >= 0, isNoMaxHeight: key == "union" || key == "filter" || key == "sql", showErrors: key == "union" },
            React.createElement(SectionDraftInput, { sectionKey: key, relation: draft.sections[key], onChange: (r) => updateSectionDraft(key, r), exprProvider: SECTION_EXPR_PROVIDERS[key] })))),
        RelationUtils.hasBQDataSource(draft.base) && (React.createElement(AccordionItem, { key: baseList.length, isOpen: openItems["code"], setIsOpen: (x) => setItemOpen("code", x), headerImageSrc: Code, headerLabel: "View Generated Query", errors: [], isNoMaxHeight: true },
            React.createElement(QueryPreview, { datasetId: datasetId })))));
};
export default RelationAccordion;
